import { computed } from "vue";
import { useTranslation } from "@/language/translation";
import { useStore } from "vuex";
import { useTranslationFunctionCode } from "@/components/menu/SidebarHelper";
import isModifierSet from "./userModifier";
import { isModuleActive } from "./moduleConfig";
import userMenu from "@/config/userMenu";
import { filterUserMenu } from "./userMenuFilter";

type MenuChildEntryType = {
  href?: string;
  icon?: string;
  title: string;
  menuKey?: string;
  badge?: { text: string; class?: string };
  child?: MenuChildEntryType[];
};

export type MenuEntryType = MenuChildEntryType & {
  callback?: (event: Event, item: MenuEntryType) => void;
};

export function useRawUserMenu() {
  const store = useStore();

  return computed(() => {
    const rights = (store.state.login.user?.accessRights ?? []) as string[];
    const anonym = store.state.login.user?.anonym === true;
    return filterUserMenu(userMenu, rights, anonym, isModifierSet, isModuleActive);
  });
}

export function useUserMenu() {
  const translationFunctionCode = useTranslationFunctionCode();
  const { t } = useTranslation();
  const rawMenu = useRawUserMenu();

  const menu = computed((): MenuEntryType[] => {
    return rawMenu.value.map((main) => {
      return {
        ...main,
        menuKey: main.title,
        title: t(
          main.translationFunctionCode != null
            ? (translationFunctionCode(main.translationFunctionCode) ??
                `sidebar.${main.title}.title`)
            : `sidebar.${main.title}.title`,
        ),
        badge: main.badge,
        icon: main.icon ?? "",
        href: main.href ?? `/redirect/${main.title}`,
        child: main.child?.map((level1) => {
          return {
            ...level1,
            menuKey: level1.title,
            href: level1.href ?? `/redirect/${level1.title}`,
            title: t(
              level1.translationFunctionCode != null
                ? (translationFunctionCode(level1.translationFunctionCode) ??
                    `sidebar.${main.title}.children.${level1.title}.title`)
                : `sidebar.${main.title}.children.${level1.title}.title`,
            ),
            child: level1.child?.map((level2) => {
              return {
                ...level2,
                menuKey: level2.title,
                href: level2.href ?? `/redirect/${level2.title}`,
                class: level2.onlyOnModifier != null ? "text-warn" : undefined,
                title:
                  (level2.onlyOnModifier != null ? "NEW_" : "") +
                  t(
                    level2.translationFunctionCode != null
                      ? (translationFunctionCode(level2.translationFunctionCode) ??
                          `sidebar.${main.title}.children.${level1.title}.children.${level2.title}.title`)
                      : `sidebar.${main.title}.children.${level1.title}.children.${level2.title}.title`,
                  ),
              };
            }),
          };
        }),
      };
    });
  });

  return {
    menu,
  };
}
